import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { ErrorBag, type ErrorBagInterface, type NamedErrorBag } from "~/util/error-bag";

export type ErrorBagState<K extends string> = ErrorBagInterface<K>;

export const ErrorBagContext = createContext<ErrorBagState<string>>(new ErrorBag<string>());

export function useErrorBag<K extends string>(): ErrorBagState<K> {
    return useContext(ErrorBagContext);
}

export const NoName = "__none__";

export function ErrorBagProvider({ children }: { children: ReactNode }) {
    const [errorBag, setErrorBag] = useState<ErrorBag<string>>(new ErrorBag<string>());

    const value = useMemo(
        () => ({
            has: (key: string) => errorBag.has(key),
            hasAny: (keys?: string | string[]) => errorBag.hasAny(keys),
            isEmpty: () => errorBag.isEmpty(),
            get: (key: string) => errorBag.get(key),
            message: (key: string) => errorBag.message(key),
            first: (key: string) => errorBag.first(key),
            forget(keys: string | string[]) {
                if (errorBag.hasAny(keys)) {
                    setErrorBag(errorBag.forget(keys).clone());
                }
            },
            add(key: string, error: string) {
                setErrorBag(errorBag.add(key, error).clone());
            },
            set(key: string, errors: string[]) {
                setErrorBag(errorBag.set(key, errors).clone());
            },
            fill(errors: NamedErrorBag<string>) {
                setErrorBag(errorBag.fill(errors).clone());
            },
            all() {
                return errorBag.all();
            },
            empty() {
                if (errorBag.hasAny()) {
                    setErrorBag(new ErrorBag<string>());
                }
            },
        }),
        [errorBag],
    );

    return <ErrorBagContext.Provider value={value}>{children}</ErrorBagContext.Provider>;
}
